import { useTranslation } from "react-i18next"
import Timeline, {
    TIMELINE_STATUS_COMPLETE, TIMELINE_STATUS_CURRENT, TIMELINE_STATUS_UPCOMING,
} from "./Timeline/Timeline"
import { FormattedRelativeTime } from "./FormattedRelativeTime"
import { buildPackageTimeline } from "../utils/buildPackageTimeline"
import { ORDER_STATUS_SEMANTIC_COLOR } from "../utils/statusColors"
import { CARRIER_NAME_CUBBO } from "../constants/shippingMethods"
import { STATUS_SHIPMENT_DELIVERED, WILL_NOT_DELIVER_SHIPMENT_STATUSES } from "../constants/orderStatuses"

// Terminal package-level states: the package will never reach "Entregado" (delivered/canceled/returned).
// Mirrors ShowOrderContainer's willNotDeliverOrder set (WILL_NOT_DELIVER_SHIPMENT_STATUSES), at minimum
// canceled and returned (#4), and is also the guard for hiding edit actions on a finished package (#15).
const TERMINAL_PACKAGE_STATUSES = [STATUS_SHIPMENT_DELIVERED, ...WILL_NOT_DELIVER_SHIPMENT_STATUSES]

export const PackageDetail = ({ order, pkg, index, isEditable = false, isCncEligible = false, onEditAddress, onPickupNearby, onViewProofOfDelivery }) => {
    const { i18n } = useTranslation()
    const nodes = buildPackageTimeline(order?.order_histories, pkg)
    const isCubbo = pkg?.carrier_name === CARRIER_NAME_CUBBO
    const isDelivered = pkg?.shipping_status === STATUS_SHIPMENT_DELIVERED
    const isPackageTerminal = TERMINAL_PACKAGE_STATUSES.includes(pkg?.shipping_status)
    // #4: a canceled/returned package will never reach "Entregado" — mirror willNotDeliverOrder.
    // Reuses the same shared set TERMINAL_PACKAGE_STATUSES is built from, so this guard stays in
    // sync automatically if WILL_NOT_DELIVER_SHIPMENT_STATUSES ever grows.
    const willNotDeliverPackage = WILL_NOT_DELIVER_SHIPMENT_STATUSES.includes(pkg?.shipping_status)
    const hasProofOfDelivery = Boolean(pkg?.proof_of_delivery || order?.proof_of_delivery)

    const buildDescription = (node) => {
        const parts = []
        const desc = node.metadata?.description
        const incidentText = desc ? i18n.t(`multiPackage.incident_description.${desc}`, { defaultValue: '' }) : ''
        if (incidentText) parts.push(<div key="inc" className="text-orange-600">{incidentText}</div>)
        if (node.metadata?.recipient_name) {
            parts.push(<div key="pod" className="text-gray-500">{i18n.t('multiPackage.received_by', { name: node.metadata.recipient_name })}</div>)
        }
        return parts.length ? <>{parts}</> : ''
    }

    // #13: fall back to multiPackage.label_status when the primary timeline title is missing
    // (some statuses, e.g. shipment_created/awaiting_collection, only have a label_status key).
    // If BOTH resolve empty, drop the node entirely rather than render a blank/timestamp-only row.
    const steps = nodes
        .map((node, i) => {
            const primaryTitle = i18n.t(`showOrder.timeline.shipping_status.${node.status}`, { defaultValue: '' })
            const title = primaryTitle || i18n.t(`multiPackage.label_status.${node.status}`, { defaultValue: '' })
            return { node, i, title }
        })
        .filter(({ title }) => title !== '')
        .map(({ node, i, title }, filteredIndex, filtered) => ({
            name: `${node.status}-${i}`,
            title,
            description: buildDescription(node),
            reference_timestamp: node.reference_timestamp,
            status: filteredIndex === filtered.length - 1 ? TIMELINE_STATUS_CURRENT : TIMELINE_STATUS_COMPLETE,
            currentStatusColor: ORDER_STATUS_SEMANTIC_COLOR[node.status] || 'green',
        }))

    // Single terminal "Entregado" upcoming node when not yet delivered and the package can still
    // reach delivery (#4: suppress it for a canceled/returned package).
    if (!isDelivered && !willNotDeliverPackage) {
        const eta = order?.estimated_time_arrival
        steps.push({
            name: 'upcoming-delivered',
            // #8: mirror the single-package tracker — surface the shared order ETA on the
            // terminal (still-upcoming) node instead of a bare "Entregado" when an ETA exists.
            title: eta ? (
                <div>
                    <span>{`${i18n.t('showOrder.estimated_delivery')} `}</span>
                    <FormattedRelativeTime
                        forceDays={true}
                        etaOffset={order?.shipping_coverage?.eta_margin_of_error_in_hours}
                        date={eta}
                        time_zone={order?.store?.warehouses?.[0]?.time_zone}
                        className="lowercase"
                    />
                </div>
            ) : i18n.t(`showOrder.timeline.shipping_status.${STATUS_SHIPMENT_DELIVERED}`),
            description: '',
            status: TIMELINE_STATUS_UPCOMING,
            reference_timestamp: null,
        })
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-gray-500">{i18n.t('orderDetails.shipping_number')}</span>
                    <span className="text-gray-900 font-mono text-xs">{pkg?.shipping_number}</span>
                </div>
                {pkg?.tracking_url && !isCubbo && (
                    <a href={pkg.tracking_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-blue-500 hover:text-blue-400">
                        {i18n.t('showOrder.view_tracking', { carrier_name: pkg.carrier_name })}
                    </a>
                )}
                {isDelivered && hasProofOfDelivery && (
                    <button type="button" onClick={onViewProofOfDelivery} className="mt-1 block text-left text-blue-500 hover:text-blue-400">
                        {i18n.t('showOrder.view_proof_of_delivery')}
                    </button>
                )}
            </div>
            {isEditable && !isPackageTerminal && (
                <div className="flex flex-col gap-1 text-sm">
                    <button type="button" onClick={onEditAddress} className="text-left text-blue-500 hover:text-blue-400">
                        {i18n.t('showOrder.edit_address')}
                    </button>
                    {isCncEligible && (
                        <button type="button" onClick={onPickupNearby} className="text-left text-blue-500 hover:text-blue-400">
                            {i18n.t('showOrder.pick_up_nearby')}
                        </button>
                    )}
                </div>
            )}
            <Timeline steps={steps} />
        </div>
    )
}
